import React, { Suspense, lazy } from 'react';
import {
    createBrowserRouter,
    Navigate,
    Outlet,
    redirect,
    useRouteError,
} from 'react-router-dom';
import RouteErrorBoundary from '../components/RouteErrorBoundary.jsx';
import RequirePermission from '../auth/RequirePermission.jsx';
import { permissions } from '@vanilla/services/PermissionService.js';
import { canAccessRoute, DEFAULT_ROUTE } from '../shell/routeAccess.js';
import RootLayout from '../shell/RootLayout.jsx';
import DealsWorkspace from '../deals/DealsWorkspace.jsx';
import { FullPageLoader } from '@/components/ui/spinner';
import NotFoundPage from '../pages/NotFoundPage.jsx';
import { DashboardProvider } from '../pages/DashboardContext.jsx';
import DashboardSwitcher from '../pages/DashboardSwitcher.jsx';

const AccountsWorkspace = lazy(() => import('../accounts/AccountsWorkspace.jsx'));
const ContactsWorkspace = lazy(() => import('../contacts/ContactsWorkspace.jsx'));
const UsersPage = lazy(() => import('../pages/UsersPage.jsx'));
const RevenuePage = lazy(() => import('../pages/RevenuePage.jsx'));
const ReportsPage = lazy(() => import('../pages/ReportsPage.jsx'));
const IntegrationsPage = lazy(() => import('../pages/IntegrationsPage.jsx'));
const DataManagementPage = lazy(() => import('../pages/DataManagementPage.jsx'));
const CommissionsPage = lazy(() => import('../pages/CommissionsPage.jsx'));
const PermissionsPage = lazy(() => import('../pages/PermissionsPage.jsx'));
const GoalsPage = lazy(() => import('../pages/GoalsPage.jsx'));
const CalendarPage = lazy(() => import('../pages/CalendarPage.jsx'));
const DashboardPage = lazy(() => import('../pages/DashboardPage.jsx'));
const LeadsWorkspace = lazy(() => import('../leads/LeadsWorkspace.jsx'));

function RouterErrorElement() {
    const error = useRouteError();
    return (
        <RouteErrorBoundary
            forceError={error instanceof Error ? error : new Error(String(error?.statusText || error?.message || 'Unknown error'))}
        />
    );
}

function Guarded({ module, children }) {
    return (
        <RouteErrorBoundary>
            <RequirePermission module={module}>
                <Suspense fallback={<FullPageLoader message="Loading�" />}>
                    {children}
                </Suspense>
            </RequirePermission>
        </RouteErrorBoundary>
    );
}

function firstAllowedPath() {
    const order = [
        'leads', 'deals', 'calendar', 'clients', 'contacts', 'dashboard', 'revenue',
        'goals', 'commissions', 'reports', 'users', 'settings',
        'permissions', 'data-management',
    ];
    for (const route of order) {
        if (canAccessRoute(route)) return `/${route}`;
    }
    return `/${DEFAULT_ROUTE}`;
}

function denyRedirect() {
    return redirect(firstAllowedPath());
}

function moduleLoader(module) {
    return () => {
        if (!permissions.isLoaded()) return null;
        if (!canAccessRoute(module)) return denyRedirect();
        return null;
    };
}

function DashboardRoute() {
    return (
        <Guarded module="dashboard">
            <DashboardProvider>
                <div className="crm-topbar hidden md:flex items-center gap-2 px-3 sm:px-4 h-12 bg-white border-b border-slate-200 shadow-sm shrink-0 sticky top-0 z-30">
                    <span className="text-sm font-bold tracking-tight shrink-0">Dashboards</span>
                    <DashboardSwitcher className="ml-2" />
                    <div className="flex-1" />
                </div>
                <div className="crm-topbar-mobile md:hidden flex items-center gap-2 px-3 sm:px-4 h-12 bg-white border-b border-slate-200 shadow-sm shrink-0 sticky top-0 z-30">
                    <span className="text-sm font-bold shrink-0">Dashboards</span>
                    <DashboardSwitcher className="ml-2" />
                </div>
                <DashboardPage />
            </DashboardProvider>
        </Guarded>
    );
}

export function createAppRouter() {
    const basename = import.meta.env.VITE_ROUTER_BASENAME || undefined;

    return createBrowserRouter(
        [
            {
                path: '/',
                element: <RootLayout />,
                errorElement: <RouterErrorElement />,
                children: [
                    { index: true, loader: () => redirect(`/${DEFAULT_ROUTE}`) },
                    {
                        path: 'leads',
                        loader: moduleLoader('leads'),
                        element: <Guarded module="leads"><LeadsWorkspace /></Guarded>,
                    },
                    {
                        path: 'deals',
                        loader: moduleLoader('deals'),
                        element: (
                            <RouteErrorBoundary>
                                <RequirePermission module="deals">
                                    <DealsWorkspace />
                                </RequirePermission>
                            </RouteErrorBoundary>
                        ),
                        children: [{ path: ':dealId', element: <Outlet /> }],
                    },
                    {
                        path: 'calendar',
                        loader: moduleLoader('calendar'),
                        element: <Guarded module="calendar"><CalendarPage /></Guarded>,
                    },
                    {
                        path: 'clients',
                        loader: moduleLoader('clients'),
                        element: <Guarded module="clients"><AccountsWorkspace /></Guarded>,
                        children: [{ path: ':clientId', element: <Outlet /> }],
                    },
                    {
                        path: 'contacts',
                        loader: moduleLoader('contacts'),
                        element: <Guarded module="contacts"><ContactsWorkspace /></Guarded>,
                        children: [{ path: ':contactId', element: <Outlet /> }],
                    },
                    { path: 'dashboard', loader: moduleLoader('dashboard'), element: <DashboardRoute /> },
                    {
                        path: 'revenue',
                        loader: moduleLoader('revenue'),
                        element: <Guarded module="revenue"><RevenuePage /></Guarded>,
                        children: [
                            { index: true, element: <Outlet /> },
                            { path: 'invoices', element: <Outlet /> },
                            { path: 'collections', element: <Outlet /> },
                            { path: 'board', element: <Outlet /> },
                        ],
                    },
                    { path: 'goals', loader: moduleLoader('goals'), element: <Guarded module="goals"><GoalsPage /></Guarded> },
                    {
                        path: 'commissions',
                        loader: moduleLoader('commissions'),
                        element: <Guarded module="commissions"><CommissionsPage /></Guarded>,
                        children: [
                            { index: true, element: <Outlet /> },
                            { path: 'rules', element: <Outlet /> },
                            { path: 'schedule', element: <Outlet /> },
                        ],
                    },
                    { path: 'reports', loader: moduleLoader('reports'), element: <Guarded module="reports"><ReportsPage /></Guarded> },
                    {
                        path: 'users',
                        loader: moduleLoader('users'),
                        element: <Guarded module="users"><UsersPage /></Guarded>,
                        children: [{ path: ':userId', element: <Outlet /> }],
                    },
                    { path: 'settings', loader: moduleLoader('settings'), element: <Guarded module="settings"><IntegrationsPage /></Guarded> },
                    { path: 'permissions', loader: moduleLoader('permissions'), element: <Guarded module="permissions"><PermissionsPage /></Guarded> },
                    { path: 'data-management', loader: moduleLoader('data-management'), element: <Guarded module="data-management"><DataManagementPage /></Guarded> },
                    { path: '*', element: <NotFoundPage /> },
                ],
            },
            {
                path: '/auth/callback',
                element: <Outlet />,
                loader: () => redirect('/'),
            },
        ],
        basename ? { basename } : undefined,
    );
}

export { firstAllowedPath, Navigate };
